import React from 'react';

// Fetch the distinct values of a facet field via the same `/:index/_search` proxy the
// runtime uses. A terms aggregation returns the buckets; size is capped because hand-
// ordering more than a few hundred values isn't practical anyway.
async function fetchFacetValues(indexes, field) {
  const index = `${indexes || ''}`.split(',').map(s => s.trim()).filter(Boolean).join(',');
  const body = {size: 0, aggs: {values: {terms: {field, size: 1000}}}};

  const response = await fetch(`/${index}/_search`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken()},
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`search failed: ${response.status}`);
  }

  const json = await response.json();
  return (json?.aggregations?.values?.buckets || [])
    .map(bucket => bucket.key)
    .filter(key => key != null)
    .map(String);
}

// Editor for a facet's value display order (stored as a newline-separated string in
// `order`). "Load values" pulls the field's distinct values from the index and appends
// any not already listed. Reorder by dragging a row, or with the ↑/↓ buttons (kept for
// keyboard/touch, which native HTML5 drag-and-drop doesn't cover); × drops a value.
// Controlled — writes the joined string back via `onChange`.
export default function FacetDisplayOrder({field, indexes, value, onChange}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const dragIndex = React.useRef(null);
  const [hoverIndex, setHoverIndex] = React.useState(null);

  const items = `${value || ''}`.split('\n').map(line => line.trim()).filter(Boolean);
  const write = next => onChange(next.join('\n'));

  const load = async () => {
    setError('');
    if (!`${field || ''}`.trim()) { setError('Set the facet attribute first.'); return; }
    if (!`${indexes || ''}`.trim()) { setError('Set the provider indexes first.'); return; }

    setLoading(true);
    try {
      const fetched = await fetchFacetValues(indexes, field);
      // Keep the current order; append values not yet listed.
      write([...items, ...fetched.filter(v => !items.includes(v))]);
    } catch (e) {
      setError('Could not load values — check the attribute is an aggregatable (keyword) field.');
    } finally {
      setLoading(false);
    }
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    write(next);
  };

  const removeAt = index => write(items.filter((_, i) => i !== index));

  // Native drag-and-drop: remove the dragged value and re-insert it at the drop row.
  const reorder = (from, to) => {
    if (from == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    write(next);
  };

  const onDrop = index => {
    reorder(dragIndex.current, index);
    dragIndex.current = null;
    setHoverIndex(null);
  };

  return (
    <div className='facet-value-order'>
      <button type='button' className='btn btn-outline-primary btn-sm' onClick={load} disabled={loading}>
        {loading ? 'Loading…' : 'Load values'}
      </button>
      {error && <small className='field-error d-block mt-1'>{error}</small>}

      {items.length > 0 && (
        <ul className='list-unstyled mt-2 mb-0'>
          {items.map((item, index) => (
            <li
              key={item}
              className='d-flex align-items-center justify-content-between border-bottom py-1'
              style={{
                cursor: 'grab',
                borderTop: hoverIndex === index && dragIndex.current !== index ? '2px solid #76b900' : '2px solid transparent',
              }}
              draggable
              onDragStart={() => { dragIndex.current = index; }}
              onDragOver={e => { e.preventDefault(); if (hoverIndex !== index) setHoverIndex(index); }}
              onDrop={e => { e.preventDefault(); onDrop(index); }}
              onDragEnd={() => { dragIndex.current = null; setHoverIndex(null); }}>
              <span className='text-white'>
                <span className='me-2' aria-hidden='true'>⠿</span>{item}
              </span>
              <span className='btn-group btn-group-sm'>
                <button type='button' className='btn btn-outline-light btn-sm' onClick={() => move(index, -1)} disabled={index === 0} aria-label='Move up'>↑</button>
                <button type='button' className='btn btn-outline-light btn-sm' onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label='Move down'>↓</button>
                <button type='button' className='btn btn-outline-warning btn-sm' onClick={() => removeAt(index)} aria-label='Remove'>×</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
